import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, Star, Zap, CheckCircle2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { t, applyDirection, resolveLocale, isRTL } from '../lib/i18n';

const API = process.env.REACT_APP_BACKEND_URL;

// Build a stable per-session visitor id (works without cookies)
const _visitorId = (() => {
  if (typeof window === 'undefined') return '';
  try {
    const k = '__klp_vid';
    let v = window.localStorage.getItem(k);
    if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); window.localStorage.setItem(k, v); }
    return v.slice(0, 32);
  } catch { return Math.random().toString(36).slice(2, 18); }
})();

function getUtm() {
  if (typeof window === 'undefined') return {};
  const qs = new URLSearchParams(window.location.search);
  return {
    utm_source: qs.get('utm_source') || '',
    utm_medium: qs.get('utm_medium') || '',
    utm_campaign: qs.get('utm_campaign') || '',
    utm_content: qs.get('utm_content') || '',
    utm_term: qs.get('utm_term') || '',
  };
}

function trackEvent(pageId, event_type, extra = {}) {
  if (!pageId) return;
  axios.post(`${API}/api/public/pages/event`, {
    page_id: pageId,
    event_type,
    visitor_id: _visitorId,
    referrer: document.referrer || '',
    user_agent: navigator.userAgent || '',
    ...getUtm(),
    ...extra,
  }).catch(() => {});
}

// Lead-capture form section
function LeadForm({ section, sectionIndex, theme, pageId, align, bg, txt, locale = 'en' }) {
  const fields = section.fields && section.fields.length ? section.fields : [
    { name: 'name', label: 'Your name', type: 'text', required: true },
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'phone', label: 'Phone (optional)', type: 'tel', required: false },
    { name: 'message', label: 'Message', type: 'textarea', required: false },
  ];
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/api/public/pages/submit-lead`, {
        page_id: pageId,
        section_index: sectionIndex,
        fields: values,
        visitor_id: _visitorId,
        referrer: document.referrer || '',
        user_agent: navigator.userAgent || '',
        ...getUtm(),
      });
      setSubmitted(true);
      // optional callback URL after submit
      if (section.button_url && section.button_url !== '#' && section.button_url.startsWith('http')) {
        setTimeout(() => { window.location.href = section.button_url; }, 1200);
      }
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || t('error_generic', locale));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: bg, color: txt }} className="py-16 px-6">
        <div className={cn('max-w-md mx-auto', align)} data-testid={`form-success-${sectionIndex}`}>
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: (theme?.primary_color || '#10B981') + '30' }}>
            <CheckCircle2 className="h-7 w-7" style={{ color: theme?.primary_color || '#10B981' }} />
          </div>
          <h3 className="text-2xl font-bold mb-2">{section.success_heading || t('thank_you', locale)}</h3>
          <p className="opacity-80">{section.success_message || section.content || "We've received your message and will be in touch shortly."}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: bg, color: txt }} className="py-16 px-6" data-testid={`lead-form-${sectionIndex}`}>
      <div className={cn('max-w-md mx-auto', align)}>
        {section.heading && <h2 className="text-3xl font-bold mb-2">{section.heading}</h2>}
        {section.subheading && <p className="opacity-80 mb-6">{section.subheading}</p>}
        <form onSubmit={submit} className="space-y-3 text-left">
          {fields.map((f, i) => (
            <div key={i}>
              {f.label && <label className="block text-xs font-medium mb-1 opacity-80">{f.label}{f.required && ' *'}</label>}
              {f.type === 'textarea' ? (
                <textarea
                  required={f.required}
                  value={values[f.name] || ''}
                  onChange={e => setValues(v => ({ ...v, [f.name]: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border bg-white/10 border-white/20 text-current placeholder-current/50 focus:outline-none focus:ring-2 focus:ring-current/20"
                  placeholder={f.placeholder || ''}
                  data-testid={`form-field-${f.name}`}
                />
              ) : (
                <input
                  type={f.type || 'text'}
                  required={f.required}
                  value={values[f.name] || ''}
                  onChange={e => setValues(v => ({ ...v, [f.name]: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border bg-white/10 border-white/20 text-current placeholder-current/50 focus:outline-none focus:ring-2 focus:ring-current/20"
                  placeholder={f.placeholder || ''}
                  data-testid={`form-field-${f.name}`}
                />
              )}
            </div>
          ))}
          {error && <p className="text-sm" style={{ color: '#ef4444' }} data-testid="form-error">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-3 rounded-lg text-white font-semibold transition disabled:opacity-50"
            style={{ backgroundColor: theme?.primary_color || '#10B981' }}
            data-testid={`form-submit-${sectionIndex}`}
          >
            {submitting ? t('sending', locale) : (section.button_text || t('submit', locale))}
          </button>
        </form>
      </div>
    </div>
  );
}

// Pure renderer — no editor chrome.
function RenderSection({ section, i, theme, pageId, locale = 'en' }) {
  const bg = section.background || '#ffffff';
  const txt = section.text_color || '#1e293b';
  const align = section.layout === 'left' ? 'text-left' : section.layout === 'right' ? 'text-right' : 'text-center';

  switch (section.type) {
    case 'html_raw':
      // Render raw HTML — no sanitization. Owner-controlled content.
      return (
        <div key={i} style={{ background: bg, color: txt }} className="px-0 py-0" data-testid={`html-section-${i}`}>
          <div dangerouslySetInnerHTML={{ __html: section.content || '' }} />
        </div>
      );
    case 'lead_form':
      return <LeadForm key={i} section={section} sectionIndex={i} theme={theme} pageId={pageId} align={align} bg={bg} txt={txt} locale={locale} />;
    case 'hero':
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-20 px-6">
          <div className={cn('max-w-5xl mx-auto', align)}>
            {section.heading && <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">{section.heading}</h1>}
            {section.subheading && <p className="text-lg sm:text-xl opacity-80 mb-5 max-w-2xl mx-auto">{section.subheading}</p>}
            {section.content && <p className="opacity-70 mb-8 max-w-2xl mx-auto">{section.content}</p>}
            {section.button_text && (
              <a href={section.button_url || '#'} style={{ backgroundColor: theme?.primary_color || '#10B981' }}
                data-cta-label={section.button_text} data-cta-section={i}
                className="inline-block px-10 py-4 rounded-lg text-white font-semibold hover:opacity-90 transition shadow-lg">
                {section.button_text}
              </a>
            )}
            {section.image_url && <img src={section.image_url} alt="" className="mt-12 rounded-xl max-w-full mx-auto shadow-2xl" />}
          </div>
        </div>
      );
    case 'features':
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-16 px-6">
          <div className={cn('max-w-6xl mx-auto', align)}>
            {section.heading && <h2 className="text-3xl font-bold mb-3">{section.heading}</h2>}
            {section.subheading && <p className="opacity-70 mb-12">{section.subheading}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(section.items || []).map((item, j) => (
                <div key={j} className="p-6 rounded-xl text-left" style={{ backgroundColor: bg === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: (theme?.primary_color || '#10B981') + '20' }}>
                    <Zap className="h-6 w-6" style={{ color: theme?.primary_color || '#10B981' }} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm opacity-70 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'cta':
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-20 px-6">
          <div className={cn('max-w-3xl mx-auto', align)}>
            {section.heading && <h2 className="text-3xl sm:text-4xl font-bold mb-3">{section.heading}</h2>}
            {section.subheading && <p className="opacity-80 mb-8 text-lg">{section.subheading}</p>}
            {section.button_text && (
              <a href={section.button_url || '#'} data-cta-label={section.button_text} data-cta-section={i}
                className="inline-block px-10 py-4 rounded-lg bg-white text-gray-900 font-semibold hover:opacity-90 transition shadow-lg">
                {section.button_text}
              </a>
            )}
          </div>
        </div>
      );
    case 'stats':
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-14 px-6">
          <div className={cn('max-w-5xl mx-auto', align)}>
            {section.heading && <h2 className="text-3xl font-bold mb-10">{section.heading}</h2>}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
              {(section.items || []).map((item, j) => (
                <div key={j}>
                  <p className="text-4xl sm:text-5xl font-bold" style={{ color: theme?.primary_color || '#10B981' }}>{item.title}</p>
                  <p className="text-sm opacity-70 mt-2">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'testimonials':
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-16 px-6">
          <div className={cn('max-w-5xl mx-auto', align)}>
            {section.heading && <h2 className="text-3xl font-bold mb-10">{section.heading}</h2>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(section.items || []).map((item, j) => (
                <div key={j} className="p-6 rounded-xl text-left" style={{ backgroundColor: bg === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
                  <div className="flex gap-1 mb-3">{[1, 2, 3, 4, 5].map(s => <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
                  <p className="italic opacity-90 mb-4">"{item.description}"</p>
                  <p className="text-sm font-semibold">{item.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'pricing':
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-16 px-6">
          <div className={cn('max-w-5xl mx-auto', align)}>
            {section.heading && <h2 className="text-3xl font-bold mb-10">{section.heading}</h2>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(section.items || []).map((item, j) => (
                <div key={j} className={cn('p-8 rounded-xl border', j === 1 ? 'border-2 shadow-xl transform md:scale-105' : '')}
                     style={{ borderColor: j === 1 ? (theme?.primary_color || '#10B981') : txt + '20' }}>
                  <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                  <p className="text-sm opacity-70 mb-6">{item.description}</p>
                  <button className="w-full py-3 rounded-lg text-white font-semibold" style={{ backgroundColor: theme?.primary_color || '#10B981' }}>Choose Plan</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'faq':
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-16 px-6">
          <div className={cn('max-w-3xl mx-auto', align)}>
            {section.heading && <h2 className="text-3xl font-bold mb-10">{section.heading}</h2>}
            <div className="space-y-3 text-left">
              {(section.items || []).map((item, j) => (
                <div key={j} className="p-5 rounded-lg" style={{ backgroundColor: bg === '#ffffff' ? '#f8fafc' : 'rgba(255,255,255,0.05)' }}>
                  <p className="font-semibold mb-2">{item.title}</p>
                  <p className="text-sm opacity-70 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'gallery':
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            {section.heading && <h2 className={cn('text-3xl font-bold mb-8', align)}>{section.heading}</h2>}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {(section.items || []).map((item, j) => (
                <div key={j} className="aspect-square rounded-lg overflow-hidden bg-gray-200">
                  {item.icon ? <img src={item.icon} alt={item.title} className="w-full h-full object-cover" /> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    case 'footer':
      return (
        <footer key={i} style={{ background: bg, color: txt }} className="py-8 px-6">
          <div className={cn('max-w-5xl mx-auto text-sm opacity-70', align)}>
            {section.content || `© ${new Date().getFullYear()}`}
          </div>
        </footer>
      );
    default:
      return (
        <div key={i} style={{ background: bg, color: txt }} className="py-12 px-6">
          <div className={cn('max-w-3xl mx-auto', align)}>
            {section.heading && <h2 className="text-2xl font-bold mb-3">{section.heading}</h2>}
            {section.content && <div className="opacity-80" dangerouslySetInnerHTML={{ __html: section.content }} />}
          </div>
        </div>
      );
  }
}

export default function PublicLandingPage() {
  const { tenantSlug, pageSlug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/pages/${tenantSlug}/${pageSlug}`);
        setPage(data);
        // Track view (with UTM)
        axios.post(`${API}/api/public/pages/track`, {
          page_id: data.id,
          referrer: document.referrer || '',
          user_agent: navigator.userAgent || '',
          screen: `${window.screen.width}x${window.screen.height}`,
          language: navigator.language || '',
          path: window.location.pathname,
          ...getUtm(),
        }).catch(() => {});
        // SEO
        if (data.seo_title) document.title = data.seo_title;
        if (data.seo_description) {
          let meta = document.querySelector('meta[name="description"]');
          if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
          meta.content = data.seo_description;
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Page not found');
      }
      setLoading(false);
    })();
  }, [tenantSlug, pageSlug]);

  // Inject custom CSS / head HTML if provided
  useEffect(() => {
    if (!page) return;
    const tags = [];
    if (page.custom_css) {
      const style = document.createElement('style');
      style.setAttribute('data-public-page', '1');
      style.textContent = page.custom_css;
      document.head.appendChild(style);
      tags.push(style);
    }
    if (page.custom_html_head) {
      const wrap = document.createElement('div');
      wrap.innerHTML = page.custom_html_head;
      const moved = [];
      Array.from(wrap.children).forEach(el => {
        document.head.appendChild(el);
        moved.push(el);
      });
      tags.push(...moved);
    }
    return () => tags.forEach(t => t.remove());
  }, [page]);

  // Auto-instrument CTA + outbound link clicks (event delegation)
  const onClickCapture = useCallback((e) => {
    if (!page) return;
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (!href || href.startsWith('#')) return;
    const isOutbound = /^https?:\/\//i.test(href) && !href.includes(window.location.host);
    const event_type = isOutbound ? 'outbound_click' : 'cta_click';
    trackEvent(page.id, event_type, {
      label: (a.dataset.ctaLabel || a.textContent || '').trim().slice(0, 80),
      target: href.slice(0, 500),
    });
  }, [page]);

  // ── Resolve locale (?lang= → page.landing_locale → navigator.language → 'en')
  // ── and apply <html dir="rtl"> when the chosen locale is RTL (Arabic, Hebrew).
  const locale = resolveLocale(page?.landing_locale);
  useEffect(() => {
    applyDirection(locale);
    // On unmount restore LTR so the rest of the SaaS (super admin pages on
    // the same SPA session) isn't accidentally flipped to RTL.
    return () => applyDirection('en');
  }, [locale]);

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-white">
      <Loader2 className="h-10 w-10 animate-spin text-emerald-500" />
    </div>
  );
  if (error) return (
    <div className="h-screen flex flex-col items-center justify-center bg-white p-6 text-center">
      <AlertCircle className="h-10 w-10 text-gray-300 mb-3" />
      <p className="text-lg font-semibold text-gray-700 mb-1">{t('page_not_found', locale)}</p>
      <p className="text-sm text-gray-500">{error}</p>
    </div>
  );
  if (!page) return null;

  // Full HTML override mode — render the entire page from a single HTML blob
  // inside an isolated iframe so platform/Tailwind styles never leak into the
  // user's template. Form submits, click tracking, and auto-resizing are
  // handled by an injected script inside the iframe.
  if (page.raw_html_override && page.raw_html_override.trim()) {
    return (
      <div data-testid="public-landing-page-html" style={{ minHeight: '100vh', background: '#fff' }}>
        <RawHtmlPage html={page.raw_html_override} pageId={page.id} />
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      onClickCapture={onClickCapture}
      data-testid="public-landing-page"
      dir={isRTL(locale) ? 'rtl' : 'ltr'}
      lang={locale}
      style={{ fontFamily: page.theme?.font || 'Inter, sans-serif' }}
    >
      {(page.sections || []).map((s, i) => <RenderSection key={i} section={s} i={i} theme={page.theme} pageId={page.id} locale={locale} />)}
    </div>
  );
}

// Renders raw HTML as the entire page body inside an isolated iframe so the
// platform's Tailwind/global styles never bleed into user-pasted templates.
// Auto-resizes to content height; form submits with data-konnect-form="1"
// are forwarded to the lead-capture endpoint via a tiny injected script.
function RawHtmlPage({ html, pageId }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(800);

  // Build the document the iframe will render. We append a tiny tracking +
  // form-binding script that talks to the parent via postMessage and to the
  // backend directly (no auth required for /api/public/* endpoints).
  const srcDoc = (() => {
    const apiUrl = JSON.stringify(API || '');
    const pid = JSON.stringify(pageId || '');
    const vid = JSON.stringify(_visitorId);
    const hasHtmlTag = /<html[\s>]/i.test(html);
    const inner = hasHtmlTag ? html : `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"></head><body style="margin:0">${html}</body></html>`;
    const injected = `
<script>
(function(){
  var API=${apiUrl}, PID=${pid}, VID=${vid};
  function utm(){ try{ var p=new URLSearchParams(parent.location.search); return {utm_source:p.get('utm_source')||'',utm_medium:p.get('utm_medium')||'',utm_campaign:p.get('utm_campaign')||'',utm_content:p.get('utm_content')||'',utm_term:p.get('utm_term')||''};}catch(e){return{};} }
  function post(url,body){ try{ var x=new XMLHttpRequest(); x.open('POST',url,true); x.setRequestHeader('Content-Type','application/json'); x.send(JSON.stringify(body)); }catch(e){} }
  // Resize observer → tell parent the body height so the iframe can grow
  function sendHeight(){
    try {
      var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, document.body.offsetHeight);
      parent.postMessage({type:'klp-height',height:h}, '*');
    } catch(e){}
  }
  if (window.ResizeObserver) { try { new ResizeObserver(sendHeight).observe(document.body); } catch(e){} }
  window.addEventListener('load', sendHeight);
  setTimeout(sendHeight, 200); setTimeout(sendHeight, 800); setTimeout(sendHeight, 2000);
  // Click tracking → outbound/cta events
  document.addEventListener('click', function(e){
    var a = e.target && (e.target.closest ? e.target.closest('a') : null);
    if (!a || !a.href) return;
    var label = (a.getAttribute('data-cta-label') || a.textContent || '').trim().slice(0,80);
    var sameOrigin = false;
    try { sameOrigin = (new URL(a.href)).origin === parent.location.origin; } catch(e){}
    var u = utm();
    post(API+'/api/public/pages/event', Object.assign({ page_id:PID, event_type: sameOrigin?'cta_click':'outbound_click', visitor_id:VID, label:label, target:a.href }, u));
  }, true);
  // Lead-form auto-bind: any <form data-konnect-form="1"> submits via API
  document.addEventListener('submit', function(e){
    var f = e.target;
    if (!f || !f.matches || !f.matches('form[data-konnect-form="1"]')) return;
    e.preventDefault();
    var fd = new FormData(f); var fields = {};
    fd.forEach(function(v,k){ fields[k]=String(v).slice(0,1000); });
    var u = utm();
    var xhr = new XMLHttpRequest();
    xhr.open('POST', API+'/api/public/pages/submit-lead', true);
    xhr.setRequestHeader('Content-Type','application/json');
    xhr.onload = function(){
      if (xhr.status >= 200 && xhr.status < 300) {
        var succ = document.querySelector('[data-konnect-success]');
        if (succ) { succ.style.display='block'; f.style.display='none'; }
        else { f.innerHTML = '<p style="padding:1rem;text-align:center;font-weight:600;color:#10B981">Thank you! We will be in touch.</p>'; }
        sendHeight();
      } else {
        var errBox = document.querySelector('[data-konnect-error]');
        var msg = 'Submission failed.';
        try { msg = (JSON.parse(xhr.responseText||'{}').detail) || msg; } catch(e){}
        if (errBox) { errBox.textContent = msg; errBox.style.display='block'; } else alert(msg);
      }
    };
    xhr.send(JSON.stringify(Object.assign({ page_id:PID, fields:fields, visitor_id:VID, referrer:document.referrer||'', user_agent:navigator.userAgent||'' }, u)));
  }, true);
})();
</script>`;
    // Inject before </body> if possible, else append.
    if (/<\/body>/i.test(inner)) return inner.replace(/<\/body>/i, injected + '</body>');
    return inner + injected;
  })();

  useEffect(() => {
    function onMsg(e) {
      if (!e.data || e.data.type !== 'klp-height') return;
      // Allow iframe content to set parent's iframe height
      const h = Math.max(400, Math.min(20000, Number(e.data.height) || 800));
      setHeight(h);
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="Landing page"
      srcDoc={srcDoc}
      sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
      style={{ width: '100%', height: height + 'px', border: 'none', display: 'block', background: '#fff' }}
      data-testid="public-landing-iframe"
    />
  );
}
